import React, { useCallback } from 'react';

// Lib
import { useEvent } from 'lib/hooks';
import { useAppDispatch } from 'lib/hooks/useDispatch';

// Actions / constants
import { showLoader } from 'reducers/loader';
import { showMessage } from 'reducers/messages';
import { messageNotEmptyValue } from 'lib/thesaurus';
import * as actionsCreator from 'pages/Search/reducer';

type UseSearchHandlersParams = {
	drv?: any;
	value: string;
	goTo?: number;
	typeData: string;
	number?: number;
	totalPages?: number;
	currentValue: string;
	setIsShowLoader: any;
};

export const useSearchHandlers = (params: UseSearchHandlersParams) => {
	const {
		drv,
		goTo,
		value,
		number,
		typeData,
		currentValue,
		totalPages,
		setIsShowLoader
	} = params;

	const dispatch = useAppDispatch();

	// -------------------
	// helpers
	// -------------------
	const showEmptyMessage = () => {
		dispatch(showMessage(messageNotEmptyValue));
	};

	const doSearch = (
		searchValue: string,
		opts: { isHistory?: boolean; type?: string } = {}
	) => {
		if (!searchValue?.length) {
			showEmptyMessage();
			return false;
		}

		dispatch(showLoader(true));
		dispatch(
			actionsCreator.searchText({
				value: searchValue,
				isHistory: opts.isHistory ?? false,
				type: opts.type ?? 'update-data'
			})
		);
		dispatch(actionsCreator.handleCurrentValue(searchValue));
		dispatch(actionsCreator.handleShowTextSearch(true));
		dispatch(actionsCreator.handleHiddenDropdown(true));

		dispatch(
			actionsCreator.historyList({
				data: searchValue,
				storeName: 'recentSearchList',
				localStoreKey: 'recentSearchList'
			})
		);

		setIsShowLoader(true);
		return true;
	};

	const runPagination = (page: number) => {
		if (typeData !== 'libraries' && page !== number) {
			dispatch(actionsCreator.handlerChangePriceCartCompound({}));
		}

		const pageHandlers: Record<string, (n: number) => any> = {
			compounds: actionsCreator.handlerChangePageCompound,
			libraries: actionsCreator.handlerChangePageLib,
			drugs: actionsCreator.handlerChangePageDrugs,
			impurities: actionsCreator.handlerChangePageImpurities,
			chemicalProbes: actionsCreator.handlerChangePageChemicalProbes
		};

		const handler = pageHandlers[typeData];
		if (handler && page !== number) {
			dispatch(handler(page));
		}

		dispatch(
			actionsCreator.searchText({
				isHistory: false,
				value: currentValue,
				type: 'update-data'
			})
		);
		dispatch(actionsCreator.handleShowTextSearch(true));
		dispatch(showLoader(true));
		setIsShowLoader(true);
	};

	// -------------------
	// pagination handlers
	// -------------------
	const onPagePress = useEvent((n: number) => runPagination(n - 1));
	const onPrevPressLast = useEvent(() => runPagination(0));
	const onNextPressLast = useEvent(() =>
		runPagination((totalPages || 0) - 1)
	);

	// -------------------
	// input handlers
	// -------------------
	const handleKeyDown = useEvent(
		(e: React.KeyboardEvent<HTMLInputElement>) => {
			if (e.key === 'Enter') {
				e.preventDefault();
				doSearch(value);
			}
		}
	);

	const handlerSubmit = useEvent(() => {
		let searchValue = value;

		if (drv?.isActive()) {
			// @ts-ignore
			searchValue = drv.getPreviousElement()?.value || value;
			drv?.moveTo(2);
			dispatch(actionsCreator.handleHiddenDropdown(true));
		}

		doSearch(searchValue);
	});

	const leftIconAction = useEvent(() => {
		doSearch(value);
	});

	const handleClickRecentItem = useEvent((v: string) => {
		doSearch(v, { isHistory: true });
	});

	const handleClickType = useCallback(
		(t: string) => {
			dispatch(actionsCreator.handlerChangeTypeData(t));
		},
		[dispatch]
	);

	const handlerSubmitGoTo = useEvent(() => {
		if (typeof goTo === 'number') {
			runPagination(goTo - 1);
		}
	});

	const onKeyPress = useEvent(
		(e: React.KeyboardEvent<HTMLInputElement>) => {
			if (e.key === 'Enter') {
				e.preventDefault();
				const inputElement = e.target as HTMLInputElement;
				runPagination(+inputElement.value - 1);
				setIsShowLoader(true);
			}
		}
	);

	const handlerChangeGoTo = useEvent(
		(event: React.ChangeEvent<HTMLInputElement>) => {
			const num = +event?.target?.value > 0 ? +event?.target?.value : 1;

			if (num <= (totalPages || 0)) {
				dispatch(
					actionsCreator.handlerChangeSearchKeyBoardGoTo({
						goTo: num,
						type: typeData
					})
				);
			} else {
				dispatch(
					showMessage({
						type: 'info',
						text: 'This is the last record'
					})
				);
			}
		}
	);

	const handlerChangeSize = useEvent((data: { value: number | string }) => {
		dispatch(
			actionsCreator.handlerChangeSearchKeyBoardSize({
				type: typeData,
				size: data.value as number
			})
		);
		runPagination(0);
	});

	const rightIconAction = useEvent(() => {
		dispatch(actionsCreator.updateSearchText(''));
	});

	// -------------------
	// return
	// -------------------
	return {
		// pagination
		onPagePress,
		onNextPressLast,
		onPrevPressLast,

		// main actions
		handleKeyDown,
		handlerSubmit,
		leftIconAction,
		handleClickType,
		handleClickRecentItem,

		onKeyPress,
		handlerSubmitGoTo,
		handlerChangeGoTo,
		handlerChangeSize,

		rightIconAction,
		getRequestPagination: runPagination
	};
};
